import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { ActionItem, Auditor, Store, ComplianceFranchise } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { Plus } from '../../../assets';
import PageArea from '../../../components/shared/PageArea';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import dayjs from 'dayjs';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';
import { useNavigate } from 'react-router-dom';
import ScrollableTableCell from '../../../components/shared/ScrollableTableCell';

interface ActionItemFormData {
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Open' | 'InProgress' | 'Resolved' | 'Closed';
  assignedToUserId: number;
  assignedToUserName: string;
  storeNumber: string;
  storeName: string;
  dueDate: string;
  auditId: number | null;
  auditName: string | null;
}

const ActionItemsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { t } = useTranslation('complianceAudits');
  const navigate = useNavigate();

  const { hasPermissionTo, selectedStore } = useContext(UserContext);

  // Permission checks
  const canCreate = hasPermissionTo(['ActionItemsCreate']);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [auditors, setAuditors] = useState<Auditor[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ActionItem | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterStore, setFilterStore] = useState<string>('');
  const [page, setPage] = useState(0);
  const [formData, setFormData] = useState<ActionItemFormData>({
    title: '',
    description: '',
    priority: 'Medium',
    status: 'Open',
    assignedToUserId: 0,
    assignedToUserName: '',
    storeNumber: selectedStore?.storeNumber || '',
    storeName: selectedStore?.storeName || '',
    dueDate: '',
    auditId: null,
    auditName: null,
  });

  const rowsPerPage = 10;
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadActionItems();
  }, [selectedStore?.storeNumber]);

  const loadActionItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const canManage = canCreate;

      const tenantFranchises: ComplianceFranchise[] = selectedStore?.storeNumber
        ? await complianceService.tenantFranchises.getAll(selectedStore.storeNumber)
        : [];
      const tenantFranchiseIds = tenantFranchises.map(f => f.franchiseId);

      const [data, auditorsData, storesData] = await Promise.all([
        complianceService.actionItems.getAll(selectedStore?.storeNumber),
        canManage ? complianceService.auditors.getAll(tenantFranchiseIds) : Promise.resolve([]),
        canManage ? complianceService.complianceStores.getAll(false, tenantFranchiseIds) : Promise.resolve([]),
      ]);
      setActionItems(data);
      setAuditors(auditorsData);
      setStores(storesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('actionItems.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setError(null);
      await complianceService.actionItems.create({
        ...formData,
        resolutionNotes: null,
        auditorNotes: null,
        closedBy: null,
        closeNotes: null,
        closedDate: null,
        createdByUserId: 0,
        createdByUserName: '',
      });
      setCreateDialogOpen(false);
      resetFormData();
      await loadActionItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('actionItems.failedToCreate'));
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      setError(null);
      await complianceService.actionItems.delete(selectedItem.id, selectedStore?.storeNumber);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      await loadActionItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('actionItems.failedToDelete'));
    }
  };

  const openCreateDialog = () => {
    resetFormData();
    setCreateDialogOpen(true);
  };

  const openDeleteDialog = (item: ActionItem) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const resetFormData = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'Medium',
      status: 'Open',
      assignedToUserId: 0,
      assignedToUserName: '',
      storeNumber: selectedStore?.storeNumber || '',
      storeName: selectedStore?.storeName || '',
      dueDate: '',
      auditId: null,
      auditName: null,
    });
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'Open': return 'error';
      case 'InProgress': return 'warning';
      case 'Resolved': return 'success';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const filteredItems = actionItems.filter(item => {
    if (filterStatus && item.status !== filterStatus) return false;
    if (filterPriority && item.priority !== filterPriority) return false;
    if (filterStore && !item.storeNumber.toLowerCase().includes(filterStore.toLowerCase())
      && !item.storeName.toLowerCase().includes(filterStore.toLowerCase())) return false;
    return true;
  });

  const maxPages = () => {
    return Math.ceil(filteredItems.length / rowsPerPage);
  };

  const paginatedItems = filteredItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  // Access guard
  if (!hasPermissionTo(['ActionItemsView'])) {
    return (
      <PageArea>
        <Alert
          sx={{ mb: 5 }}
          severity="warning">{t('common.noPermission')}</Alert>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <PageTitle subTitle={t('actionItems.pageSubTitle')}>
          {t('actionItems.pageTitle')}
        </PageTitle>
        {canCreate && (
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={openCreateDialog}
          >
            {t('actionItems.newActionItem')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 5 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {hasPermissionTo(['StoreScopedAuditsView']) && (
          <TextField
            label={t('common.store')}
            value={filterStore}
            onChange={(e) => {
              setFilterStore(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
            placeholder={t('actionItems.filterByStore')}
          />
        )}
        <TextField
          select
          label={t('common.status')}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('common.allStatuses')}</MenuItem>
          <MenuItem value="Open">{t('common.open')}</MenuItem>
          <MenuItem value="InProgress">{t('common.inProgress')}</MenuItem>
          <MenuItem value="Resolved">{t('common.resolved')}</MenuItem>
          <MenuItem value="Closed">{t('common.closed')}</MenuItem>
        </TextField>
        <TextField
          select
          label={t('common.priority')}
          value={filterPriority}
          onChange={(e) => {
            setFilterPriority(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('common.allPriorities')}</MenuItem>
          <MenuItem value="Critical">{t('common.critical')}</MenuItem>
          <MenuItem value="High">{t('common.high')}</MenuItem>
          <MenuItem value="Medium">{t('common.medium')}</MenuItem>
          <MenuItem value="Low">{t('common.low')}</MenuItem>
        </TextField>
      </Box>

      <Section>
        {filteredItems.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('actionItems.noItems')}
            searchTerm={filterStatus || filterPriority || filterStore}
            onClear={() => {
              setFilterStatus('');
              setFilterPriority('');
              setFilterStore('');
              setPage(0);
            }}
            onAdd={openCreateDialog}
            onAddIsCreating={false}
            addItemText={t('actionItems.newActionItem')}
            onAddIsDisabled={!canCreate}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('actionItems.pageTitle')}
                </Typography>
                <Pill text={t('actionItems.itemCount', { count: filteredItems.length })} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 800, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '35%' }}>{t('actionItems.title')}</TableCell>
                    <TableCell sx={{ width: '20%' }}>{t('common.assignedTo')}</TableCell>
                    <TableCell sx={{ width: '12%' }}>{t('common.priority')}</TableCell>
                    <TableCell sx={{ width: '12%' }}
                      align="center">{t('common.status')}</TableCell>
                    <TableCell sx={{ width: '12%' }}>{t('common.dueDate')}</TableCell>
                    <TableCell sx={{ width: '9%' }}
                      align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedItems.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.custom.gray[50],
                        },
                      }}
                      onClick={() => navigate(`/compliance/audits/action-items/${item.id}`, { state: { from: 'actionItems' } })}
                    >
                      <ScrollableTableCell>
                        <Tooltip
                          title={item.description}
                          placement="top"
                        >
                          <Box>
                            <Box
                              sx={{
                                fontSize: '0.75rem',
                                color: theme.palette.custom.gray[500],
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.auditName || '-'}
                            </Box>
                            <Box sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{item.title}</Box>
                          </Box>
                        </Tooltip>
                      </ScrollableTableCell>
                      <TableCell>
                        <Box>
                          <Box
                            sx={{
                              fontSize: '0.75rem',
                              color: theme.palette.custom.gray[500],
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.storeName}
                          </Box>
                          <Box sx={{ fontWeight: 500 }}>{item.assignedToUserName}</Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.priority}
                          color={getPriorityColor(item.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.status}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {item.status === 'Closed' && item.resolvedDate
                          ? new Date(item.resolvedDate).toLocaleDateString()
                          : item.dueDate
                            ? new Date(item.dueDate).toLocaleDateString()
                            : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/compliance/audits/action-items/${item.id}`, { state: { from: 'actionItems' } });
                            }}
                            title={t('common.view')}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteDialog(item);
                            }}
                            title={t('common.delete')}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      {/* Create Action Item Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('actionItems.createActionItem')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('actionItems.title')}
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label={t('common.description')}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            fullWidth
            required
          />
          <TextField
            label={t('common.store')}
            value={formData.storeNumber}
            onChange={(e) => {
              const store = stores.find(s => s.storeNumber === e.target.value);
              setFormData({ ...formData, storeNumber: e.target.value, storeName: store?.storeName || '' });
            }}
            select
            fullWidth
            required
          >
            {stores.map((store) => (
              <MenuItem key={store.id}
                value={store.storeNumber}>
                {store.storeName} ({store.storeNumber})
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('common.priority')}
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as ActionItemFormData['priority'] })}
              select
              fullWidth
              required
            >
              <MenuItem value="Low">{t('common.low')}</MenuItem>
              <MenuItem value="Medium">{t('common.medium')}</MenuItem>
              <MenuItem value="High">{t('common.high')}</MenuItem>
              <MenuItem value="Critical">{t('common.critical')}</MenuItem>
            </TextField>
            <TextField
              label={t('common.status')}
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as ActionItemFormData['status'] })}
              select
              fullWidth
              required
            >
              <MenuItem value="Open">{t('common.open')}</MenuItem>
              <MenuItem value="InProgress">{t('common.inProgress')}</MenuItem>
              <MenuItem value="Resolved">{t('common.resolved')}</MenuItem>
              <MenuItem value="Closed">{t('common.closed')}</MenuItem>
            </TextField>
          </Box>
          <TextField
            label={t('common.assignedTo')}
            value={formData.assignedToUserId}
            onChange={(e) => {
              const userId = Number(e.target.value);
              const auditor = auditors.find(a => a.id === userId);
              setFormData({ ...formData, assignedToUserId: userId, assignedToUserName: auditor?.name || '' });
            }}
            select
            fullWidth
            required
          >
            {auditors.map((auditor) => (
              <MenuItem key={auditor.id}
                value={auditor.id}>
                {auditor.name} — {auditor.role}
              </MenuItem>
            ))}
          </TextField>
          <DatePickerLocalized
            useCalendar={false}
            label={t('common.dueDate')}
            value={formData.dueDate}
            onChange={(newValue) => setFormData({ ...formData, dueDate: newValue || '' })}
            minDate={dayjs(today)}
            translations={{
              clearButtonLabel: t('calendar.clearButtonLabel'),
              todayButtonLabel: t('calendar.todayButtonLabel'),
              cancelButtonLabel: t('calendar.cancelButtonLabel'),
              okButtonLabel: t('calendar.okButtonLabel'),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            size="lg"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            variant="primary"
            size="lg"
            disabled={!formData.title.trim() || !formData.description.trim() || !formData.dueDate || !formData.assignedToUserId}
          >
            {t('actionItems.createActionItem')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
        nameOfObject={selectedItem?.title || t('actionItems.actionItem')}
      />
    </PageArea>
  );
};

export default ActionItemsPage;
